// Insight overlays — ONE inspect mode with swappable data "lenses" over a shared visual grammar.
//
// Grammar (three reusable renderers, so every lens looks like one system):
//   • HeatLayer     — soft-tile heatmap on a set of ground cells (paths or enclosures), bad→good ramp.
//   • MarkerLayer   — floating glass pills (icon + value) above problem/insight spots. DOM, pooled.
//   • CoverageLayer — soft radial glow + crisp ring + radar sweep for point-source reach (vet).
//   • FlowLayer     — streaming, shimmering arrows for guest movement (reads the visitor flow field).
//
// A lens just supplies DATA to those renderers; the look is fixed. Recompute is throttled (dirty by
// time), per-frame work is only cheap animation (opacity/scale/projection) → protects the framerate.
import * as THREE from 'three';
import { store } from '../core/store.js';
import { bus } from '../core/bus.js';
import { softTileTexture, softDiscTexture, arrowTexture, rampColor, RAMP, pulse } from './overlayStyle.js';
import { SPECIES } from './animalCatalog.js';
import { bloodlinePct } from './animalRules.js';
import { AMENITIES } from './amenityCatalog.js';

// Tone palette (mirrors panels.css semantics + categorical need hues).
export const TONE = {
  bad: '#fb7185', mid: '#fbbf24', good: '#34d399', info: '#7dd3fc', care: '#22d3ee', money: '#f6c651',
  hunger: '#fb923c', thirst: '#38bdf8', toilet: '#a78bfa', fun: '#f472b6',
};
const SERVE_CAT = { food: 'food', drink: 'drink', fun: 'fun', toilet: 'facility', care: 'facility' };

// Lens catalogue (metadata the panel reads: icon, label, question, legend). Compute lives in OverlaySystem.
export const LENSES = [
  { id: 'happiness', icon: '😊', label: 'Guest Happiness', q: 'Where are guests happy or fed up?', legend: [['bad', 'Unhappy'], ['mid', 'OK'], ['good', 'Delighted']] },
  { id: 'food',    icon: '🍔', label: 'Food',    q: 'Where can guests grab food?',   legend: [['bad', 'No food'], ['mid', 'Far'], ['good', 'Covered']] },
  { id: 'drinks',  icon: '🥤', label: 'Drinks',  q: 'Where can guests get a drink?', legend: [['bad', 'No drinks'], ['mid', 'Far'], ['good', 'Covered']] },
  { id: 'toilets', icon: '🚻', label: 'Toilets', q: 'Are toilets within reach?',     legend: [['bad', 'None near'], ['mid', 'Far'], ['good', 'Covered']] },
  { id: 'funl',    icon: '🎡', label: 'Fun',     q: 'Where is there fun to be had?', legend: [['bad', 'Boring'], ['mid', 'Far'], ['good', 'Covered']] },
  { id: 'garbage', icon: '🗑️', label: 'Bins',    q: 'Is a bin within ~15 steps?',    legend: [['bad', 'No bin near'], ['mid', 'Far'], ['good', 'In reach']] },
  { id: 'lighting',icon: '💡', label: 'Lighting', q: 'Which paths are lit?',           legend: [['bad', 'Dark'], ['mid', 'Dim'], ['good', 'Lit']] },
  { id: 'seating', icon: '🪑', label: 'Seating',  q: 'Is a bench within ~15 steps?',   legend: [['bad', 'Nowhere to rest'], ['mid', 'Far'], ['good', 'In reach']] },
  { id: 'wellbeing', icon: '🐾', label: 'Animal Wellbeing', q: 'Which habitats thrive or suffer?',   legend: [['bad', 'Suffering'], ['mid', 'OK'], ['good', 'Thriving']] },
  { id: 'dna',       icon: '🧬', label: 'Bloodlines (DNA)', q: 'Which bloodlines are pure or inbred?', legend: [['bad', 'Inbred'], ['mid', 'Mixed'], ['good', 'Pure']] },
  { id: 'money',     icon: '💰', label: 'Revenue',          q: "What's earning and what's idle?",     legend: [['bad', 'Idle'], ['mid', 'OK'], ['good', 'Top earner']] },
  { id: 'service',   icon: '🛎️', label: 'Service Quality',  q: 'Which services are understaffed?',    legend: [['bad', 'Understaffed'], ['mid', 'OK'], ['good', 'Premium']] },
  { id: 'traffic',   icon: '🚶', label: 'Guest Flow',       q: 'Where do guests walk and jam up?',    legend: [['info', 'Idle'], ['good', 'Calm'], ['mid', 'Busy'], ['bad', 'Packed']] },
  { id: 'health',    icon: '🏥', label: 'Vet Coverage',     q: 'Which animals are in vet range?',     legend: [['care', 'In range'], ['bad', 'Needs care']] },
];

// ---- geometry helpers ----
function cellWorld(t, cx, cz, y = 0) {
  const half = t.worldSize / 2;
  const wx = (cx + 0.5) * t.cellSize - half, wz = (cz + 0.5) * t.cellSize - half;
  return new THREE.Vector3(wx, t.heightAtWorld(wx, wz) + y, wz);
}
function encCentroid(t, enc) {
  let sx = 0, sz = 0, n = 0;
  enc.cells.forEach((k) => { const c = k.indexOf(','); sx += +k.slice(0, c); sz += +k.slice(c + 1); n++; });
  return n ? { cx: sx / n, cz: sz / n } : { cx: 0, cz: 0 };
}

// ============================ HeatLayer ============================
class HeatLayer {
  constructor(scene) { this.scene = scene; this.mesh = null; this.cap = 0; this.base = 0.6; }
  _ensure(n) {
    if (this.mesh && this.cap >= n) return;
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.geometry.dispose(); this.mesh.material.dispose(); }
    this.cap = Math.max(n, this.cap * 2, 128);
    const geo = new THREE.PlaneGeometry(1, 1); geo.rotateX(-Math.PI / 2);
    this.mesh = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({ map: softTileTexture(), transparent: true, opacity: this.base, depthWrite: false }), this.cap);
    this.mesh.renderOrder = 3; this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }
  set(cells, t, scale = 1.3) {
    if (!cells.length) { this.clear(); return; }
    this._ensure(cells.length);
    const m = new THREE.Matrix4(), p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3(), col = new THREE.Color();
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i]; p.copy(cellWorld(t, c.cx, c.cz, 0.2)); s.setScalar(t.cellSize * scale);
      m.compose(p, q, s); this.mesh.setMatrixAt(i, m);
      if (c.hex) col.set(c.hex); else rampColor(col, c.t);
      this.mesh.setColorAt(i, col);
    }
    this.mesh.count = cells.length;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor.needsUpdate = true;
    this.mesh.visible = true;
  }
  animate() { if (this.mesh && this.mesh.visible) this.mesh.material.opacity = this.base * pulse(0.7, 0.86, 1); }
  clear() { if (this.mesh) this.mesh.visible = false; }
  dispose() { if (this.mesh) { this.scene.remove(this.mesh); this.mesh.geometry.dispose(); this.mesh.material.dispose(); this.mesh = null; } }
}

// ============================ MarkerLayer (DOM pills) ============================
class MarkerLayer {
  constructor(world) {
    this.world = world;
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:7;overflow:hidden';
    document.body.appendChild(this.root);
    this.pool = []; this.data = []; this._v = new THREE.Vector3();
  }
  set(markers) { this.data = markers || []; }
  clear() { this.data = []; for (const el of this.pool) el.style.display = 'none'; }
  _pill() {
    const d = document.createElement('div');
    d.style.cssText = 'position:absolute;transform:translate(-50%,-100%);display:flex;align-items:center;gap:6px;padding:4px 10px 4px 8px;border-radius:999px;background:rgba(12,17,23,.74);backdrop-filter:blur(9px) saturate(150%);-webkit-backdrop-filter:blur(9px) saturate(150%);border:1px solid rgba(255,255,255,.16);box-shadow:0 5px 16px rgba(0,0,0,.42);font:600 12px/1 ui-sans-serif,system-ui,sans-serif;color:#fff;white-space:nowrap;will-change:transform,opacity';
    const dot = document.createElement('span'); dot.style.cssText = 'width:8px;height:8px;border-radius:50%;flex:0 0 auto';
    const ic = document.createElement('span'); ic.style.cssText = 'font-size:14px;line-height:1';
    const tx = document.createElement('span');
    d.append(dot, ic, tx); d._dot = dot; d._ic = ic; d._tx = tx;
    this.root.appendChild(d); return d;
  }
  update() {
    const cam = this.world.renderer.camera, dom = this.world.renderer.renderer.domElement, rect = dom.getBoundingClientRect();
    const now = performance.now();
    const cand = [];
    for (const mk of this.data) {
      const dist = cam.position.distanceTo(mk.pos); if (dist > 320) continue;
      this._v.copy(mk.pos).project(cam);
      if (this._v.z > 1 || Math.abs(this._v.x) > 1.05 || Math.abs(this._v.y) > 1.05) continue;
      cand.push({ mk, dist, x: rect.left + (this._v.x * 0.5 + 0.5) * rect.width, y: rect.top + (-this._v.y * 0.5 + 0.5) * rect.height });
    }
    cand.sort((a, b) => a.dist - b.dist);
    if (cand.length > 26) cand.length = 26;
    for (let i = 0; i < cand.length; i++) {
      let el = this.pool[i]; if (!el) { el = this._pill(); this.pool.push(el); }
      const { mk, dist } = cand[i];
      el.style.display = 'flex';
      el._dot.style.background = TONE[mk.tone] || '#fff';
      el._dot.style.boxShadow = `0 0 8px ${TONE[mk.tone] || '#fff'}`;
      el._ic.textContent = mk.icon || '';
      el._tx.textContent = mk.label || '';
      const bob = Math.sin(now * 0.003 + i) * 3;
      el.style.left = Math.round(cand[i].x) + 'px';
      el.style.top = Math.round(cand[i].y + bob) + 'px';
      el.style.opacity = dist > 260 ? 0.45 : 1;
    }
    for (let i = cand.length; i < this.pool.length; i++) this.pool[i].style.display = 'none';
  }
  dispose() { this.root.remove(); }
}

// ============================ CoverageLayer (world discs) ============================
class CoverageLayer {
  constructor(scene) { this.scene = scene; this.items = []; }
  _clear() { for (const it of this.items) { this.scene.remove(it.fill); this.scene.remove(it.ring); this.scene.remove(it.sweep); it.fill.geometry.dispose(); it.fill.material.dispose(); it.ring.geometry.dispose(); it.ring.material.dispose(); it.sweep.geometry.dispose(); it.sweep.material.dispose(); } this.items = []; }
  set(discs) {
    this._clear();
    for (const d of discs) {
      const hex = TONE[d.tone] || TONE.care;
      const fill = new THREE.Mesh(new THREE.PlaneGeometry(d.r * 2, d.r * 2), new THREE.MeshBasicMaterial({ map: softDiscTexture(), color: hex, transparent: true, opacity: 0.2, side: THREE.DoubleSide, depthWrite: false }));
      const ring = new THREE.Mesh(new THREE.RingGeometry(d.r - 2.2, d.r, 72), new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.65, side: THREE.DoubleSide, depthWrite: false }));
      const sweep = new THREE.Mesh(new THREE.RingGeometry(d.r - 2.2, d.r, 72), new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false }));
      for (const m of [fill, ring, sweep]) { m.rotation.x = -Math.PI / 2; m.renderOrder = 3; m.frustumCulled = false; }
      fill.position.copy(d.pos); fill.position.y += 0.12;
      ring.position.copy(d.pos); ring.position.y += 0.16;
      sweep.position.copy(d.pos); sweep.position.y += 0.18;
      this.scene.add(fill); this.scene.add(ring); this.scene.add(sweep);
      this.items.push({ fill, ring, sweep });
    }
  }
  animate() {
    const p = pulse(0.9, 0.72, 1), ph = (performance.now() % 2600) / 2600;
    for (const it of this.items) {
      it.ring.material.opacity = 0.65 * p;
      it.sweep.scale.setScalar(0.04 + ph * 0.98);
      it.sweep.material.opacity = (1 - ph) * 0.5;
    }
  }
  clear() { this._clear(); }
  dispose() { this._clear(); }
}

// ============================ FlowLayer (streaming arrows) ============================
class FlowLayer {
  constructor(world) {
    this.world = world; this.mesh = null; this._cells = null; this._rebuildT = 99;
    const geo = new THREE.PlaneGeometry(1, 1); geo.rotateX(-Math.PI / 2); this.geo = geo;
    this.mat = new THREE.MeshBasicMaterial({ map: arrowTexture(), transparent: true, depthWrite: false, opacity: 0.95 });
  }
  active(dt) {
    const t = this.world.terrain, half = t.worldSize / 2, flow = this.world.flow;
    // streaming shimmer every 2 frames
    if (this.mesh && this._cells) {
      this._shim = (this._shim || 0) + 1;
      if (this._shim % 2 === 0) {
        const ts = performance.now() * 0.001, col = this._tc || (this._tc = new THREE.Color());
        for (const c of this._cells) { const b = 0.22 + 0.95 * (0.5 + 0.5 * Math.sin((c.cx + c.cz) * 0.85 - ts * 4.2)); col.setRGB(Math.min(1, c.r * b), Math.min(1, c.g * b), Math.min(1, c.b * b)); this.mesh.setColorAt(c.i, col); }
        this.mesh.instanceColor.needsUpdate = true;
      }
    }
    this._rebuildT += dt; if (this.mesh && this._rebuildT < 1) return; this._rebuildT = 0;
    if (this.mesh) { this.world.renderer.scene.remove(this.mesh); this.mesh = null; }
    const cells = [...this.world.build.paths]; if (!cells.length) { this._cells = null; return; }
    const im = new THREE.InstancedMesh(this.geo, this.mat, cells.length); im.frustumCulled = false; im.renderOrder = 3;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3(), col = new THREE.Color(), up = new THREE.Vector3(0, 1, 0);
    const rec = [];
    cells.forEach((key, i) => {
      const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1);
      const wx = (cx + 0.5) * t.cellSize - half, wz = (cz + 0.5) * t.cellSize - half;
      const d = flow ? flow.data.get(key) : null, traffic = d ? d.count : 0;
      const yaw = d && (Math.abs(d.dx) + Math.abs(d.dz)) > 0.1 ? Math.atan2(d.dx, d.dz) : 0;
      q.setFromAxisAngle(up, yaw + Math.PI);
      p.set(wx, t.heightAtWorld(wx, wz) + 0.3, wz);
      const load = Math.min(1, traffic / 12); s.setScalar(t.cellSize * (0.62 + 0.3 * load));
      m.compose(p, q, s); im.setMatrixAt(i, m);
      if (traffic < 0.5) col.setHex(RAMP.idle); else rampColor(col, 1 - load);
      im.setColorAt(i, col); rec.push({ i, cx, cz, r: col.r, g: col.g, b: col.b });
    });
    im.instanceColor.needsUpdate = true; this.world.renderer.scene.add(im); this.mesh = im;
    this._cells = rec.length <= 15000 ? rec : null;
  }
  clear() { if (this.mesh) { this.world.renderer.scene.remove(this.mesh); this.mesh = null; } this._cells = null; this._rebuildT = 99; }
  dispose() {
    this.clear();
    this.geo.dispose();
    this.mat.map?.dispose?.();
    this.mat.dispose();
  }
}

// ============================ OverlaySystem ============================
export class OverlaySystem {
  constructor(world) {
    this.world = world;
    const scene = world.renderer.scene;
    this.heat = new HeatLayer(scene);
    this.markers = new MarkerLayer(world);
    this.coverage = new CoverageLayer(scene);
    this.flow = new FlowLayer(world);
    this._active = null; this._recomputeT = 999; this._flowOn = false;
    this.income = new Map();     // "cx,cz" -> rolling income (money lens)
    this._subs = [
      bus.on('ui:sale', ({ cx, cz, amount }) => { if (cx == null) return; const k = cx + ',' + cz; this.income.set(k, (this.income.get(k) || 0) + amount); }),
      bus.on('sim:newDay', () => { for (const [k, v] of this.income) { const nv = v * 0.5; if (nv < 1) this.income.delete(k); else this.income.set(k, nv); } }),
    ];
  }
  dispose() { this._subs.forEach((u) => u()); this.heat.dispose(); this.markers.dispose(); this.coverage.dispose(); this.flow.dispose(); }

  _clearAll() { this.heat.clear(); this.markers.clear(); this.coverage.clear(); this.flow.clear(); this._flowOn = false; }

  update(dt) {
    const lens = store.get().overlayLens || null;
    if (lens !== this._active) { this._active = lens; this._clearAll(); this._recomputeT = 999; }
    if (!lens) return;
    this._recomputeT += dt;
    const period = ['food', 'drinks', 'toilets', 'funl', 'garbage', 'lighting', 'seating', 'happiness'].includes(lens) ? 0.8 : 0.4;
    if (this._recomputeT >= period) { this._recomputeT = 0; this._recompute(lens); }
    this.heat.animate();
    this.markers.update();
    this.coverage.animate();
    if (this._flowOn) this.flow.active(dt);
  }

  _recompute(lens) {
    const fn = this['_lens_' + lens]; if (!fn) return;
    const out = fn.call(this, this.world) || {};
    // heat
    if (out.heat && out.heat.length) this.heat.set(out.heat, this.world.terrain, out.heatScale || 1.3); else this.heat.clear();
    // markers
    this.markers.set(out.markers || []);
    // coverage
    if (out.coverage && out.coverage.length) this.coverage.set(out.coverage); else this.coverage.clear();
    // flow
    if (out.flow) { this._flowOn = true; } else { this._flowOn = false; this.flow.clear(); }
    bus.emit('overlay:info', { lens, summary: out.summary || '' });
  }

  // ---------------- lenses ----------------
  _lens_happiness(w) {
    const R = 12, serves = ['hunger', 'thirst', 'toilet', 'fun'];
    const props = [['bin', 15], ['lamp', 12], ['bench', 15]];
    const fade = (arr, cx, cz, RR) => { if (!arr || !arr.length) return 0; let d = 1e9; for (let j = 0; j < arr.length; j++) { const dd = Math.max(Math.abs(arr[j][0] - cx), Math.abs(arr[j][1] - cz)); if (dd < d) { d = dd; if (d === 0) break; } } return d <= RR ? 1 : Math.max(0, 1 - (d - RR) / RR); };
    const src = { hunger: [], thirst: [], toilet: [], fun: [] };
    if (w.amenities) for (const [k, it] of w.amenities.byCell) { if (it.closed) continue; const sv = (AMENITIES[it.type] || {}).serves; if (src[sv]) { const c = k.indexOf(','); src[sv].push([+k.slice(0, c), +k.slice(c + 1)]); } }
    const psrc = { bin: [], lamp: [], bench: [] };
    if (w.props) for (const it of w.props.items) { if (psrc[it.type]) psrc[it.type].push([it.cx, it.cz]); }
    const V = w.visitors ? w.visitors.visitors : [];
    const byCell = new Map(); let g = 0;
    for (const v of V) { g += v.happiness; const k = v.cell.cx + ',' + v.cell.cz; let o = byCell.get(k); if (!o) byCell.set(k, o = { s: 0, n: 0 }); o.s += v.happiness; o.n++; }
    g = V.length ? g / V.length : null;
    const heat = []; let predSum = 0;
    for (const key of w.build.paths) {
      const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1);
      // Predicted happiness mirrors the sim: needs (food/drinks/toilets/fun) dominant, comfort
      // (bins/lighting/seating) tops it off — so the whole path network reads even with few guests.
      let needSum = 0; for (const sv of serves) needSum += fade(src[sv], cx, cz, R);
      let comSum = 0; for (const p of props) comSum += fade(psrc[p[0]], cx, cz, p[1]);
      const base = 0.2 + 0.6 * (needSum / 4) + 0.2 * (comSum / 3);
      predSum += base;
      const o = byCell.get(key);
      heat.push({ cx, cz, base, local: o ? (o.s / o.n / 100) : null });
    }
    // Anchor the map to the ACTUAL average happiness shown in the Visitors panel: shift the predicted
    // field so its mean equals the real guest average, then let cells with guests show their local truth.
    const bias = (g != null && heat.length) ? (g / 100 - predSum / heat.length) : 0;
    for (const h of heat) {
      let t = Math.max(0, Math.min(1, h.base + bias));
      if (h.local != null) t = 0.5 * t + 0.5 * h.local;
      h.t = t;
    }
    const bad = []; for (const [k, o] of byCell) if (o.n >= 2 && o.s / o.n < 42) bad.push({ k, avg: o.s / o.n });
    bad.sort((a, b) => a.avg - b.avg);
    const markers = bad.slice(0, 12).map(({ k, avg }) => { const c = k.indexOf(','), cx = +k.slice(0, c), cz = +k.slice(c + 1); return { pos: cellWorld(w.terrain, cx, cz, 2.6), icon: '😠', label: Math.round(avg) + '%', tone: 'bad' }; });
    const summary = g == null ? 'Predicted from need coverage · no guests yet' : `Avg happiness ${Math.round(g)}% · ${bad.length} unhappy spot${bad.length === 1 ? '' : 's'}`;
    return { heat, heatScale: 1.15, markers, summary };
  }

  // Prop coverage (bins / lighting / seating): fraction of path cells within R of a placed prop.
  // Kept deliberately generous (R≈15) so a handful of props cover the network — no need to blanket the map.
  _propLens(w, type, icon, label, R) {
    const src = []; let outlets = 0;
    if (w.props) for (const it of w.props.items) { if (it.type === type) { outlets++; src.push([it.cx, it.cz]); } }
    const heat = []; let served = 0, np = 0, worst = null;
    for (const key of w.build.paths) {
      const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1); np++;
      let d = 1e9;
      for (let j = 0; j < src.length; j++) { const dd = Math.max(Math.abs(src[j][0] - cx), Math.abs(src[j][1] - cz)); if (dd < d) { d = dd; if (d === 0) break; } }
      const t = d <= R ? 1 : Math.max(0, 1 - (d - R) / R);
      if (d <= R) served++;
      heat.push({ cx, cz, t });
      if (!worst || d > worst.d) worst = { cx, cz, d };
    }
    const pct = np ? served / np : 1;
    const markers = [];
    if (worst && (outlets === 0 || (pct < 0.8 && worst.d > R))) markers.push({ pos: cellWorld(w.terrain, worst.cx, worst.cz, 2.6), icon, label: outlets ? 'Place here' : 'Place a ' + label.toLowerCase().replace(/s$/, ''), tone: 'bad' });
    return { heat, heatScale: 1.15, markers, summary: `${icon} ${label}: ${Math.round(pct * 100)}% within reach · ${outlets} placed` };
  }
  _lens_garbage(w) { return this._propLens(w, 'bin', '🗑️', 'Bins', 15); }
  _lens_lighting(w) { return this._propLens(w, 'lamp', '💡', 'Lighting', 12); }
  _lens_seating(w) { return this._propLens(w, 'bench', '🪑', 'Seating', 15); }

  // Per-need coverage (Food/Drinks/Toilets/Fun). FOOTPRINT-AWARE: distance is measured to the nearest
  // covered cell of every OPEN serving building (amenities.byCell holds each footprint cell), matching
  // the visitor buy reach (Chebyshev ≤ R). Recolors paths bad→good and marks the worst gap.
  _needLens(w, serve, icon, label) {
    const R = 12;
    const srcCells = []; let outlets = 0;
    for (const it of w.amenities.items) { if (it.closed) continue; const def = AMENITIES[it.type]; if (def && def.serves === serve) outlets++; }
    for (const [k, it] of w.amenities.byCell) {
      if (it.closed) continue; const def = AMENITIES[it.type];
      if (def && def.serves === serve) { const c = k.indexOf(','); srcCells.push([+k.slice(0, c), +k.slice(c + 1)]); }
    }
    const heat = []; let served = 0, np = 0, worst = null;
    for (const key of w.build.paths) {
      const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1); np++;
      let d = 1e9;
      for (let j = 0; j < srcCells.length; j++) { const dd = Math.max(Math.abs(srcCells[j][0] - cx), Math.abs(srcCells[j][1] - cz)); if (dd < d) { d = dd; if (d === 0) break; } }
      const t = d <= R ? 1 : Math.max(0, 1 - (d - R) / R);   // full within reach, fades to 0 by ~2×R
      if (d <= R) served++;
      heat.push({ cx, cz, t });
      if (!worst || d > worst.d) worst = { cx, cz, d };
    }
    const pct = np ? served / np : 1;
    const markers = [];
    if (worst && (outlets === 0 || (pct < 0.75 && worst.d > R))) markers.push({ pos: cellWorld(w.terrain, worst.cx, worst.cz, 2.6), icon, label: outlets ? 'Build here' : 'Build ' + label.toLowerCase(), tone: 'bad' });
    return { heat, heatScale: 1.15, markers, summary: `${icon} ${label}: ${Math.round(pct * 100)}% of paths covered · ${outlets} outlet${outlets === 1 ? '' : 's'}` };
  }
  _lens_food(w) { return this._needLens(w, 'hunger', '🍔', 'Food'); }
  _lens_drinks(w) { return this._needLens(w, 'thirst', '🥤', 'Drinks'); }
  _lens_toilets(w) { return this._needLens(w, 'toilet', '🚻', 'Toilets'); }
  _lens_funl(w) { return this._needLens(w, 'fun', '🎡', 'Fun'); }

  _byEnc(w) {
    const map = new Map();
    for (const a of w.animals.animals) { if (a.escaped) continue; let o = map.get(a.enclosureId); if (!o) map.set(a.enclosureId, o = { well: 0, n: 0, sp: {} }); o.well += (a.wellbeing || 0); o.n++; o.sp[a.species] = (o.sp[a.species] || 0) + 1; }
    return map;
  }

  _lens_wellbeing(w) {
    const per = this._byEnc(w), heat = [], markers = []; let sum = 0, tot = 0, risk = 0;
    for (const enc of w.build.enclosures) {
      const o = per.get(enc.id); if (!o || !o.n) continue;
      const avg = o.well / o.n; sum += o.well; tot += o.n;
      enc.cells.forEach((k) => { const c = k.indexOf(','); heat.push({ cx: +k.slice(0, c), cz: +k.slice(c + 1), t: avg / 100 }); });
      if (avg < 45) { risk++; const ctr = encCentroid(w.terrain, enc); const sp = Object.keys(o.sp).sort((a, b) => o.sp[b] - o.sp[a])[0]; markers.push({ pos: cellWorld(w.terrain, ctr.cx, ctr.cz, 3.2), icon: '😟', label: `${SPECIES[sp] ? SPECIES[sp].label : 'Animals'} ${Math.round(avg)}%`, tone: avg < 30 ? 'bad' : 'mid' }); }
    }
    return { heat, heatScale: 1.3, markers, summary: `Avg wellbeing ${tot ? Math.round(sum / tot) : 0}% · ${risk} habitat${risk === 1 ? '' : 's'} at risk` };
  }

  _lens_dna(w) {
    const per = this._byEnc(w), heat = [], markers = []; let sumP = 0, nLines = 0, inbred = 0, morphs = 0;
    for (const a of w.animals.animals) if (a.morph && !a.escaped) morphs++;
    const seen = new Set();
    for (const enc of w.build.enclosures) {
      const o = per.get(enc.id); if (!o || !o.n) continue;
      const sp = Object.keys(o.sp).sort((a, b) => o.sp[b] - o.sp[a])[0]; const pct = bloodlinePct(sp);
      enc.cells.forEach((k) => { const c = k.indexOf(','); heat.push({ cx: +k.slice(0, c), cz: +k.slice(c + 1), t: pct / 100 }); });
      if (!seen.has(sp)) { seen.add(sp); sumP += pct; nLines++; if (pct < 60) inbred++; }
      const ctr = encCentroid(w.terrain, enc);
      markers.push({ pos: cellWorld(w.terrain, ctr.cx, ctr.cz, 3.2), icon: '🧬', label: `${SPECIES[sp] ? SPECIES[sp].label : '?'} ${pct}%`, tone: pct < 60 ? 'bad' : pct < 80 ? 'mid' : 'good' });
    }
    if (markers.length > 20) markers.length = 20;
    return { heat, heatScale: 1.3, markers, summary: `Avg bloodline ${nLines ? Math.round(sumP / nLines) : 100}% · ${inbred} inbred · ${morphs} rare morph${morphs === 1 ? '' : 's'}` };
  }

  _lens_money(w) {
    const items = w.amenities.items.filter((i) => i.type !== 'entry');
    let max = 1; for (const it of items) { const v = this.income.get(it.cx + ',' + it.cz) || 0; if (v > max) max = v; }
    const heat = [], earners = [];
    for (const it of items) {
      const v = this.income.get(it.cx + ',' + it.cz) || 0; const t = v / max;
      const fp = w.amenities._footprintCells(it.type, it.cx, it.cz);
      fp.forEach(([cx, cz]) => heat.push({ cx, cz, t }));
      if (v > 0) earners.push({ it, v });
    }
    earners.sort((a, b) => b.v - a.v);
    const markers = earners.slice(0, 6).map(({ it, v }) => ({ pos: cellWorld(w.terrain, it.cx, it.cz, 3), icon: '💰', label: '$' + Math.round(v).toLocaleString('en-US'), tone: 'money' }));
    const top = earners[0];
    return { heat, heatScale: 1.15, markers, summary: earners.length ? `Top earner: ${AMENITIES[top.it.type] ? AMENITIES[top.it.type].label : 'shop'} $${Math.round(top.v).toLocaleString('en-US')}/day` : 'No sales yet — open the gates!' };
  }

  _lens_service(w) {
    const eco = w.economy; if (!eco) return { heat: [], markers: [] };
    const catPct = {}; for (const cat of ['food', 'drink', 'fun', 'facility']) catPct[cat] = eco.categoryQualityPct ? eco.categoryQualityPct(cat) : 100;
    const heat = [], markers = [], shown = {};
    for (const it of w.amenities.items) {
      if (it.type === 'entry') continue; const cat = SERVE_CAT[(AMENITIES[it.type] || {}).serves]; if (!cat) continue;
      const t = catPct[cat] / 100;
      w.amenities._footprintCells(it.type, it.cx, it.cz).forEach(([cx, cz]) => heat.push({ cx, cz, t }));
      if (catPct[cat] < 50 && !shown[cat]) { shown[cat] = 1; markers.push({ pos: cellWorld(w.terrain, it.cx, it.cz, 3), icon: '⚠️', label: `${cat} ${catPct[cat]}%`, tone: 'bad' }); }
    }
    return { heat, heatScale: 1.15, markers, summary: `🍔 ${catPct.food}% · 🥤 ${catPct.drink}% · 🎡 ${catPct.fun}% · 🚻 ${catPct.facility}%` };
  }

  _lens_traffic(w) {
    const flow = w.flow; const markers = []; let busiest = 0;
    if (flow) { const hot = []; for (const [k, d] of flow.data) { if (d.count > busiest) busiest = d.count; if (d.count > 10) hot.push({ k, c: d.count }); } hot.sort((a, b) => b.c - a.c); for (const h of hot.slice(0, 8)) { const c = h.k.indexOf(','), cx = +h.k.slice(0, c), cz = +h.k.slice(c + 1); markers.push({ pos: cellWorld(w.terrain, cx, cz, 2.4), icon: '🚶', label: 'Busy', tone: h.c > 18 ? 'bad' : 'mid' }); } }
    return { flow: true, markers, summary: busiest > 10 ? `Congestion building on ${markers.length} path${markers.length === 1 ? '' : 's'}` : 'Traffic flowing freely' };
  }

  _lens_health(w) {
    const vets = w.amenities.items.filter((i) => i.type === 'vet');
    const coverage = vets.map((v) => ({ pos: (v.model ? v.model.position.clone() : cellWorld(w.terrain, v.cx, v.cz, 0)), r: 60, tone: 'care' }));
    const per = this._byEnc(w), markers = []; let covered = 0, tot = 0;
    for (const enc of w.build.enclosures) {
      const o = per.get(enc.id); if (!o || !o.n) continue; tot++;
      const ctr = encCentroid(w.terrain, enc); const cw = cellWorld(w.terrain, ctr.cx, ctr.cz, 0);
      const inRange = coverage.some((c) => c.pos.distanceTo(cw) <= c.r);
      if (inRange) covered++;
      const avg = o.well / o.n;
      if (avg < 50) markers.push({ pos: cellWorld(w.terrain, ctr.cx, ctr.cz, 3.2), icon: inRange ? '🤒' : '🚑', label: inRange ? 'Recovering' : 'No vet', tone: inRange ? 'mid' : 'bad' });
    }
    return { coverage, markers, summary: `${vets.length} vet clinic${vets.length === 1 ? '' : 's'} · ${tot ? Math.round(covered / tot * 100) : 0}% of habitats covered` };
  }
}
